import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { scrollState } from "@/lib/scrollState";
import { SCENES, LINKS } from "@/content/story";
import { INDIA_GEO } from "./indiaOutline";

const V = (x, y, z) => new THREE.Vector3(x, y, z);

/* ---------------- colour grading per chapter ---------------- */

const MOODS = [
    { t: 0.0, top: "#d3e2d8", bot: "#fbe9c8", sun: "#ffd9a0", i: 1.3 },
    { t: 0.2, top: "#c2ddd2", bot: "#f5eccd", sun: "#ffe8c2", i: 1.35 },
    { t: 0.4, top: "#a9c8d8", bot: "#f2ead2", sun: "#fff0d0", i: 1.25 },
    { t: 0.58, top: "#ccd8c4", bot: "#f2e6c6", sun: "#ffe2ae", i: 1.25 },
    { t: 0.74, top: "#565c5a", bot: "#878d88", sun: "#9fa6a3", i: 0.5 },
    { t: 0.87, top: "#e8b98a", bot: "#f8d9a8", sun: "#ffc879", i: 1.4 },
    { t: 0.95, top: "#e8b98a", bot: "#f8d9a8", sun: "#ffc879", i: 1.35 },
    { t: 1.0, top: "#33414f", bot: "#dca86e", sun: "#f0b070", i: 0.85 },
];

const GROUND_ZONES = [
    { z0: 100, z1: -95, c: ["#8fae60", "#a3bb74", "#d9c25a"] },
    { z0: -95, z1: -160, c: ["#96b26e", "#aabb8a", "#c2a44e"] },
    { z0: -160, z1: -215, c: ["#9caf88", "#b3c39a", "#8a9078"] },
    { z0: -215, z1: -255, c: ["#8d8477", "#9a9184", "#7a736a"] },
    { z0: -255, z1: -400, c: ["#7ba367", "#8fb27a", "#5e8a52"] },
];

/* ---------------- helpers ---------------- */

const n2 = (x, y) => {
    const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return s - Math.floor(s);
};

const vnoise = (x, y) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const u = xf * xf * (3 - 2 * xf);
    const v = yf * yf * (3 - 2 * yf);
    const a = n2(xi, yi);
    const b = n2(xi + 1, yi);
    const c = n2(xi, yi + 1);
    const d = n2(xi + 1, yi + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
};

const groundH = (x, z) => {
    const edge = THREE.MathUtils.smoothstep(Math.abs(x), 15, 34);
    return (
        vnoise(x * 0.05, z * 0.05) * 2.6 * edge +
        vnoise(x * 0.16, z * 0.16) * 0.9 * edge
    );
};

const sampleMood = (t) => {
    for (let i = 1; i < MOODS.length; i++) {
        if (t <= MOODS[i].t) {
            const a = MOODS[i - 1];
            const b = MOODS[i];
            const k = THREE.MathUtils.clamp((t - a.t) / (b.t - a.t || 1), 0, 1);
            return { a, b, k };
        }
    }
    return { a: MOODS[MOODS.length - 1], b: MOODS[MOODS.length - 1], k: 0 };
};

const zoneW = (p, a, b, c, d) => {
    if (p <= a || p >= d) return 0;
    if (p < b) return (p - a) / (b - a);
    if (p <= c) return 1;
    return 1 - (p - c) / (d - c);
};

const std = (color, extra = {}) =>
    new THREE.MeshStandardMaterial({
        color,
        roughness: 0.9,
        metalness: 0,
        ...extra,
    });

/* ---------------- hand-painted canvas textures ---------------- */

const canvasTexture = (w, h, draw) => {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    draw(ctx, w, h);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

const speckle = (ctx, w, h, r, g, b, n, a) => {
    for (let i = 0; i < n; i++) {
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.beginPath();
        ctx.arc(Math.random() * w, Math.random() * h, 1 + Math.random() * 2.4, 0, 7);
        ctx.fill();
    }
};

const mudWallTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#dcb28c";
        ctx.fillRect(0, 0, w, h);
        speckle(ctx, w, h, 0, 0, 0, 900, 0.05);
        speckle(ctx, w, h, 255, 240, 220, 500, 0.06);
        ctx.strokeStyle = "rgba(120,85,55,0.16)";
        ctx.lineWidth = 2;
        for (let i = 0; i < 5; i++) {
            ctx.beginPath();
            ctx.moveTo(0, 30 + i * 52 + Math.random() * 10);
            ctx.bezierCurveTo(w * 0.3, 20 + i * 52, w * 0.6, 44 + i * 52, w, 28 + i * 52);
            ctx.stroke();
        }
    });

const clayTileTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#b0552d";
        ctx.fillRect(0, 0, w, h);
        for (let y = 0; y < 8; y++) {
            for (let x = 0; x < 8; x++) {
                ctx.fillStyle = `rgb(${172 + Math.random() * 26}, ${78 + Math.random() * 18}, ${42 + Math.random() * 12})`;
                ctx.beginPath();
                ctx.roundRect(x * 32 + 2, y * 32 + 2, 28, 28, 9);
                ctx.fill();
            }
        }
    });

const woodTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#8a5f3c";
        ctx.fillRect(0, 0, w, h);
        for (let i = 0; i < 30; i++) {
            ctx.strokeStyle = `rgba(${100 + Math.random() * 50}, ${66 + Math.random() * 30}, ${36 + Math.random() * 20}, 0.5)`;
            ctx.lineWidth = 2 + Math.random() * 3;
            ctx.beginPath();
            ctx.moveTo(0, Math.random() * h);
            ctx.bezierCurveTo(w * 0.3, Math.random() * h, w * 0.6, Math.random() * h, w, Math.random() * h);
            ctx.stroke();
        }
    });

const plasterTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#f0e9d8";
        ctx.fillRect(0, 0, w, h);
        speckle(ctx, w, h, 0, 0, 0, 700, 0.04);
        speckle(ctx, w, h, 255, 250, 235, 400, 0.05);
        ctx.fillStyle = "rgba(120,150,170,0.06)";
        for (let i = 0; i < 6; i++) {
            ctx.fillRect(Math.random() * w, 0, 12 + Math.random() * 22, h);
        }
    });

const concreteTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#a09a8e";
        ctx.fillRect(0, 0, w, h);
        speckle(ctx, w, h, 0, 0, 0, 900, 0.07);
        speckle(ctx, w, h, 255, 255, 250, 500, 0.06);
    });

const dirtTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#a5764e";
        ctx.fillRect(0, 0, w, h);
        speckle(ctx, w, h, 0, 0, 0, 1100, 0.08);
        speckle(ctx, w, h, 235, 200, 150, 700, 0.07);
    });

const glassTex = () =>
    canvasTexture(256, 256, (ctx, w, h) => {
        ctx.fillStyle = "#9fb2b5";
        ctx.fillRect(0, 0, w, h);
        for (let y = 0; y < 8; y++) {
            for (let x = 0; x < 4; x++) {
                ctx.fillStyle = `rgba(40, 62, 74, ${0.5 + Math.random() * 0.3})`;
                ctx.fillRect(x * 64 + 8, y * 32 + 6, 48, 20);
            }
        }
    });

const charpaiTex = () =>
    canvasTexture(128, 128, (ctx, w, h) => {
        ctx.fillStyle = "#e2d4ac";
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = "rgba(160, 120, 70, 0.55)";
        for (let i = 0; i < w; i += 10) ctx.fillRect(i, 0, 4, h);
        for (let i = 0; i < h; i += 10) ctx.fillRect(0, i, w, 4);
    });

const signTex = (title, sub) =>
    canvasTexture(1024, 320, (ctx, w, h) => {
        ctx.fillStyle = "#f6f1e3";
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = "#2f5a7a";
        ctx.lineWidth = 14;
        ctx.strokeRect(10, 10, w - 20, h - 20);
        ctx.fillStyle = "#2b2927";
        ctx.font = "bold 92px Georgia, serif";
        const measured = ctx.measureText(title).width;
        if (measured > w - 140) {
            const fit = Math.floor(92 * ((w - 140) / measured));
            ctx.font = `bold ${fit}px Georgia, serif`;
        }
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(title, w / 2, sub ? h / 2 - 34 : h / 2);
        if (sub) {
            ctx.fillStyle = "#c85a32";
            ctx.font = "600 40px 'JetBrains Mono', monospace";
            ctx.fillText(sub.toUpperCase(), w / 2, h / 2 + 62);
        }
    });

const panelTex = (title, desc) =>
    canvasTexture(640, 400, (ctx, w, h) => {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, "#fdf9ee");
        grad.addColorStop(1, "#f2e8d2");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = "#c85a32";
        ctx.fillRect(0, 0, w, 14);
        ctx.textAlign = "center";
        ctx.fillStyle = "#2b2927";
        ctx.font = "bold 54px Georgia, serif";
        const words = title.split(" ");
        let tl = "";
        const tlines = [];
        words.forEach((word) => {
            const test = tl ? `${tl} ${word}` : word;
            if (ctx.measureText(test).width > w - 90) {
                tlines.push(tl);
                tl = word;
            } else tl = test;
        });
        tlines.push(tl);
        tlines.forEach((l, i) => ctx.fillText(l, w / 2, 108 + i * 58));
        ctx.fillStyle = "#68635C";
        ctx.font = "30px 'JetBrains Mono', monospace";
        const dwords = desc.split(" ");
        let dl = "";
        const dlines = [];
        dwords.forEach((word) => {
            const test = dl ? `${dl} ${word}` : word;
            if (ctx.measureText(test).width > w - 110) {
                dlines.push(dl);
                dl = word;
            } else dl = test;
        });
        dlines.push(dl);
        dlines.forEach((l, i) => ctx.fillText(l, w / 2, h - 130 + i * 42));
        ctx.fillStyle = "#c85a32";
        ctx.font = "600 24px 'JetBrains Mono', monospace";
        ctx.fillText("· MY WORK ·", w / 2, h - 30);
    });

/* ---------------- the boy — storybook style: round head, curly hair, capsule limbs ---------------- */

export function buildCharacter(shirt = "#c85a32", withBackpack = false) {
    const g = new THREE.Group();
    const skin = std("#e2ab7c");
    const hairM = std("#201a16");
    const shirtM = std(shirt);
    const pantsM = std("#4a5a40");
    const darkM = std("#1d1b19");
    const woodM = std("#8a5f3c");

    // kurta torso + collar
    const torso = new THREE.Mesh(
        new RoundedBoxGeometry(0.52, 0.68, 0.32, 4, 0.1),
        shirtM
    );
    torso.position.y = 1.04;
    const hem = new THREE.Mesh(new RoundedBoxGeometry(0.56, 0.1, 0.36, 3, 0.04), shirtM);
    hem.position.y = 0.72;
    const collar = new THREE.Mesh(new RoundedBoxGeometry(0.5, 0.09, 0.3, 3, 0.04), std("#f0e9d8"));
    collar.position.y = 1.36;

    // round head + curly hair
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24, 14, 12), skin);
    head.scale.set(1, 1.06, 0.96);
    head.position.y = 1.6;
    const hairCap = new THREE.Mesh(new THREE.SphereGeometry(0.265, 12, 10, 0, Math.PI * 2, 0, Math.PI * 0.62), hairM);
    hairCap.position.y = 1.6;
    hairCap.scale.set(1, 0.95, 1);
    for (let i = 0; i < 7; i++) {
        const curl = new THREE.Mesh(new THREE.SphereGeometry(0.055 + n2(i, 3) * 0.03, 7, 6), hairM);
        const a = (i / 7) * Math.PI * 2;
        curl.position.set(Math.cos(a) * 0.17, 1.68 + n2(i, 7) * 0.1, Math.sin(a) * 0.14 - 0.05);
        g.add(curl);
    }

    // expressive face
    const whiteM = new THREE.MeshBasicMaterial({ color: "#f9f6ee" });
    const blackM = new THREE.MeshBasicMaterial({ color: "#141210" });
    [-0.095, 0.095].forEach((ex) => {
        const brow = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.02, 0.02), hairM);
        brow.position.set(ex, 1.655, 0.208);
        brow.rotation.z = ex > 0 ? -0.1 : 0.1;
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.042, 8, 8), whiteM);
        eye.scale.set(1, 1, 0.4);
        eye.position.set(ex, 1.615, 0.185);
        const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.02, 6, 6), blackM);
        pupil.position.set(ex, 1.615, 0.208);
        g.add(brow, eye, pupil);
    });
    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 6), skin);
    nose.position.set(0, 1.575, 0.22);
    const smile = new THREE.Mesh(new THREE.TorusGeometry(0.055, 0.011, 6, 12, Math.PI), blackM);
    smile.position.set(0, 1.5, 0.2);
    smile.rotation.x = Math.PI;
    smile.rotation.z = Math.PI;

    // glasses
    const lensM = new THREE.MeshStandardMaterial({
        color: "#bfe0e8",
        transparent: true,
        opacity: 0.45,
        roughness: 0.15,
    });
    const glasses = new THREE.Group();
    [-0.1, 0.1].forEach((gx) => {
        const frame = new THREE.Mesh(new THREE.TorusGeometry(0.062, 0.013, 6, 14), darkM);
        frame.position.set(gx, 0, 0);
        const lens = new THREE.Mesh(new THREE.CircleGeometry(0.056, 12), lensM);
        lens.position.set(gx, 0, 0.004);
        glasses.add(frame, lens);
    });
    glasses.add(new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.022, 0.022), darkM));
    [-0.17, 0.17].forEach((tx) => {
        const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.011, 0.011, 0.28, 4), darkM);
        arm.rotation.x = Math.PI / 2;
        arm.position.set(tx, 0.02, -0.13);
        glasses.add(arm);
    });
    glasses.position.set(0, 1.615, 0.19);

    // capsule limbs
    const armL = new THREE.Group();
    armL.position.set(-0.31, 1.3, 0);
    const armLM = new THREE.Mesh(new THREE.CapsuleGeometry(0.062, 0.34, 4, 8), shirtM);
    armLM.position.y = -0.22;
    armL.add(armLM);
    const handL = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), skin);
    handL.position.y = -0.47;
    armL.add(handL);

    const armR = new THREE.Group();
    armR.position.set(0.31, 1.3, 0);
    const armRM = new THREE.Mesh(new THREE.CapsuleGeometry(0.062, 0.34, 4, 8), shirtM);
    armRM.position.y = -0.22;
    armR.add(armRM);
    const handR = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), skin);
    handR.position.y = -0.47;
    armR.add(handR);

    const legL = new THREE.Group();
    legL.position.set(-0.13, 0.74, 0);
    const legLM = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.4, 4, 8), pantsM);
    legLM.position.y = -0.26;
    legL.add(legLM);
    const shoeL = new THREE.Mesh(new RoundedBoxGeometry(0.17, 0.1, 0.27, 3, 0.045), darkM);
    shoeL.position.set(0, -0.53, 0.05);
    legL.add(shoeL);

    const legR = new THREE.Group();
    legR.position.set(0.13, 0.74, 0);
    const legRM = new THREE.Mesh(new THREE.CapsuleGeometry(0.075, 0.4, 4, 8), pantsM);
    legRM.position.y = -0.26;
    legR.add(legRM);
    const shoeR = new THREE.Mesh(new RoundedBoxGeometry(0.17, 0.1, 0.27, 3, 0.045), darkM);
    shoeR.position.set(0, -0.53, 0.05);
    legR.add(shoeR);

    g.add(torso, hem, collar, head, hairCap, glasses, nose, smile, armL, armR, legL, legR);

    if (withBackpack) {
        const pack = new THREE.Mesh(new RoundedBoxGeometry(0.4, 0.5, 0.2, 3, 0.06), std("#8a5f3c"));
        pack.position.set(0, 1.1, -0.26);
        const strap = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.4, 0.02), std("#5a4632"));
        strap.position.set(0.12, 1.18, 0.17);
        const strap2 = strap.clone();
        strap2.position.x = -0.12;
        g.add(pack, strap, strap2);
    }

    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return { group: g, parts: { torso, head, armL, armR, legL, legR } };
}

/* ---------------- organic canopies ---------------- */

const displacedCanopy = (r, detail, amp) => {
    const geo = new THREE.IcosahedronGeometry(r, detail);
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const k = 1 + (vnoise(x * 2 + r, y * 2 - z) - 0.5) * amp;
        pos.setXYZ(i, x * k, y * k, z * k);
    }
    geo.computeVertexNormals();
    return geo;
};

/* ---------------- main scene ---------------- */

export class JourneyScene {
    constructor(container, opts = {}) {
        this.container = container;
        this.opts = opts;
        this.progress = 0;
        this.elapsed = 0;
        this.time = 0;
        this.pointer = { x: 0, y: 0 };
        this.pointerSmooth = { x: 0, y: 0 };
        this.characters = [];
        this.hotspots = [];
        this.hovered = null;
        this.panels = [];
        this.birds = [];
        this.raycaster = new THREE.Raycaster();
        this.tmpV = new THREE.Vector3();
        this.tmpA = new THREE.Vector3();
        this.tmpB = new THREE.Vector3();
        this.flyInput = 0;
        this.explore = { active: false, yaw: 0, pitch: 0, vel: new THREE.Vector3(), keys: {}, blend: 1, fromPos: new THREE.Vector3() };
        this.dragging = false;

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            powerPreference: "high-performance",
        });
        const isMobile = window.innerWidth < 768;
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.6));
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.08;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        container.appendChild(this.renderer.domElement);

        this.mudTex = mudWallTex();
        this.clayTex = clayTileTex();
        this.woodTex = woodTex();
        this.plasterTex = plasterTex();
        this.concreteTex = concreteTex();
        this.dirtTex = dirtTex();
        this.glassTex = glassTex();
        this.charpaiTex = charpaiTex();

        this.scene = new THREE.Scene();
        this.fog = new THREE.Fog(0xfbe9c8, 45, 175);
        this.scene.fog = this.fog;

        this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 520);
        this.camera.position.set(11, 9, 21);

        this.hemi = new THREE.HemisphereLight(0xffe8c8, 0x8a7a5a, 0.8);
        this.scene.add(this.hemi);
        this.ambient = new THREE.AmbientLight(0xfff0da, 0.4);
        this.scene.add(this.ambient);
        this.sun = new THREE.DirectionalLight(0xffd9a0, 1.35);
        this.sun.position.set(24, 34, 8);
        this.sun.castShadow = true;
        this.sun.shadow.mapSize.set(isMobile ? 1024 : 2048, isMobile ? 1024 : 2048);
        this.sun.shadow.camera.left = -40;
        this.sun.shadow.camera.right = 40;
        this.sun.shadow.camera.top = 40;
        this.sun.shadow.camera.bottom = -40;
        this.sun.shadow.camera.near = 1;
        this.sun.shadow.camera.far = 140;
        this.sun.shadow.bias = -0.0008;
        this.scene.add(this.sun, this.sun.target);

        this.buildSky();
        this.buildGround();
        this.buildCameraRail();
        this.buildVillage();
        this.buildSchool();
        this.buildAirfield();
        this.buildCity();
        this.buildStormRoad();
        this.buildDawnHome();
        this.buildProjects();
        this.buildClouds();
        this.buildParticles();

        window.addEventListener("resize", (this.onResize = () => this.resize()));
        window.addEventListener("pointermove", (this.onPointer = (e) => {
            this.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
            this.pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
            if (this.explore.active) {
                if (document.pointerLockElement) {
                    this.explore.yaw -= e.movementX * 0.0022;
                    this.explore.pitch = THREE.MathUtils.clamp(
                        this.explore.pitch - e.movementY * 0.0022, -1.35, 1.35);
                } else if (this.dragging) {
                    this.explore.yaw -= e.movementX * 0.003;
                    this.explore.pitch = THREE.MathUtils.clamp(
                        this.explore.pitch - e.movementY * 0.003, -1.35, 1.35);
                }
            } else {
                this.handleHover(e);
            }
        }));
        window.addEventListener("mousedown", (this.onMouseDown = () => { this.dragging = true; }));
        window.addEventListener("mouseup", (this.onMouseUp = () => { this.dragging = false; }));
        window.addEventListener("keydown", (this.onKeyDown = (e) => {
            this.explore.keys[e.code] = true;
            if (e.code === "Escape" && this.explore.active) this.exitExplore();
        }));
        window.addEventListener("keyup", (this.onKeyUp = (e) => { this.explore.keys[e.code] = false; }));
        window.addEventListener("click", (this.onClick = (e) => {
            if (e.target.closest && e.target.closest("button, a")) return;
            if (this.explore.active) {
                if (!document.pointerLockElement) this.renderer.domElement.requestPointerLock?.();
                return;
            }
            if (this.hovered && this.hovered.userData.link) {
                window.open(this.hovered.userData.link, "_blank", "noopener");
            } else if (this.hovered && this.hovered.userData.sceneP != null) {
                this.scrollToProgress(this.hovered.userData.sceneP);
            }
        }));
        this.renderer.domElement.addEventListener("touchstart", (this.onTouchStart = (e) => {
            if (!this.explore.active) return;
            e.preventDefault();
            this.lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }, { passive: false }));
        this.renderer.domElement.addEventListener("touchmove", (this.onTouchMove = (e) => {
            if (!this.explore.active || !this.lastTouch) return;
            e.preventDefault();
            const tch = e.touches[0];
            this.explore.yaw -= (tch.clientX - this.lastTouch.x) * 0.006;
            this.explore.pitch = THREE.MathUtils.clamp(
                this.explore.pitch - (tch.clientY - this.lastTouch.y) * 0.006, -1.35, 1.35);
            this.lastTouch = { x: tch.clientX, y: tch.clientY };
        }, { passive: false }));

        this.clock = new THREE.Clock();
        this.tick = this.tick.bind(this);
        this.raf = requestAnimationFrame(this.tick);
        this.resize();
        this.opts.onReady?.(this);
    }

    /* ---------- sky ---------- */

    buildSky() {
        const geo = new THREE.SphereGeometry(400, 24, 16);
        this.skyMat = new THREE.ShaderMaterial({
            uniforms: {
                topColor: { value: new THREE.Color("#d3e2d8") },
                bottomColor: { value: new THREE.Color("#fbe9c8") },
            },
            vertexShader:
                "varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
            fragmentShader:
                "varying vec3 vP; uniform vec3 topColor; uniform vec3 bottomColor; void main(){ float h = clamp(normalize(vP).y * 0.5 + 0.5, 0.0, 1.0); gl_FragColor = vec4(mix(bottomColor, topColor, pow(h, 0.75)), 1.0); }",
            side: THREE.BackSide,
            depthWrite: false,
        });
        this.scene.add(new THREE.Mesh(geo, this.skyMat));
    }

    /* ---------- terrain ---------- */

    buildGround() {
        const geo = new THREE.PlaneGeometry(120, 420, 48, 132);
        geo.rotateX(-Math.PI / 2);
        geo.translate(0, 0, -150);
        const pos = geo.attributes.position;
        const colors = new Float32Array(pos.count * 3);
        const c = new THREE.Color();
        for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i);
            const z = pos.getZ(i);
            pos.setY(i, groundH(x, z));

            const zone =
                GROUND_ZONES.find((zn) => z <= zn.z0 && z > zn.z1) ||
                GROUND_ZONES[GROUND_ZONES.length - 1];
            const k = n2(x * 0.3, z * 0.3);
            c.set(zone.c[0]);
            if (k > 0.5) c.lerp(new THREE.Color(zone.c[1]), (k - 0.5) / 0.5);
            if (k > 0.84) c.lerp(new THREE.Color(zone.c[2]), (k - 0.84) / 0.16);
            colors[i * 3] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;
        }
        geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        const ground = new THREE.Mesh(
            geo,
            new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1 })
        );
        ground.receiveShadow = true;
        this.scene.add(ground);
    }

    buildCameraRail() {
        const camPts = [
            V(9, 5, 16), V(2, 3.5, -8), V(-7, 4.5, -32), V(-5.5, 3, -55),
            V(-4.2, 2.6, -66), V(-2, 3, -84), V(5, 2.2, -103), V(3.5, 9, -124),
            V(4, 11, -134), V(-2, 14, -152), V(-2, 13, -170), V(2, 7, -196),
            V(0, 4.5, -214), V(-2.5, 3.4, -236), V(2.5, 4.2, -262), V(7.5, 5.5, -287),
            V(4, 4.6, -312), V(0, 4.6, -324),
        ];
        const lookPts = [
            V(2, 2.5, -6), V(0, 2, -30), V(-4, 2.5, -55), V(-3, 2, -71),
            V(-3, 2, -74), V(0, 1.5, -100), V(0, 1.2, -118), V(0, 6, -134),
            V(0, 7.5, -138), V(0, 10, -158), V(0, 8, -180), V(0, 4, -204),
            V(0, 2.5, -228), V(0, 2, -240), V(4, 1.8, -280.5), V(3.8, 1.5, -279),
            V(2, 3, -305), V(0, 4.5, -341),
        ];
        this.camCurve = new THREE.CatmullRomCurve3(camPts, false, "catmullrom", 0.5);
        this.lookCurve = new THREE.CatmullRomCurve3(lookPts, false, "catmullrom", 0.5);
    }

    registerHotspot(group, sceneId, sceneP) {
        group.userData.isHotspot = true;
        group.userData.sceneId = sceneId;
        group.userData.sceneP = sceneP;
        group.userData.baseScale = group.scale.x;
        this.hotspots.push(group);
    }

    /* ---------- shared storybook pieces ---------- */

    // rounded barrel-tile roof — the curved terracotta look
    makeDomeRoof(w, d, h, tex) {
        const roof = new THREE.Mesh(
            new THREE.SphereGeometry(Math.max(w, d) * 0.62, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
            new THREE.MeshStandardMaterial({ map: tex, roughness: 0.92 })
        );
        roof.scale.set(w / (Math.max(w, d) * 0.62) * 1.04, h, d / (Math.max(w, d) * 0.62) * 1.04);
        roof.castShadow = true;
        return roof;
    }

    makeHut(x, z, ry, glow, s = 1) {
        const g = new THREE.Group();
        const base = new THREE.Mesh(
            new RoundedBoxGeometry(3, 2.2, 2.6, 4, 0.14),
            new THREE.MeshStandardMaterial({ map: this.mudTex, roughness: 0.95 })
        );
        base.position.y = 1.1;
        base.castShadow = true;
        const roof = this.makeDomeRoof(3.2, 2.8, 1.15, this.clayTex);
        roof.position.y = 2.16;
        const door = new THREE.Mesh(new RoundedBoxGeometry(0.72, 1.25, 0.1, 3, 0.05), new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.9 }));
        door.position.set(-0.5, 0.63, 1.31);
        const doorFrame = new THREE.Mesh(new RoundedBoxGeometry(0.92, 1.38, 0.08, 3, 0.04), std("#4a7a9b"));
        doorFrame.position.set(-0.5, 0.66, 1.3);
        const winMat = new THREE.MeshBasicMaterial({
            color: new THREE.Color("#3d3831").lerp(new THREE.Color("#ffd489"), glow),
        });
        const win = new THREE.Mesh(new RoundedBoxGeometry(0.52, 0.52, 0.06, 3, 0.04), winMat);
        win.position.set(0.8, 1.45, 1.31);
        const winFrame = new THREE.Mesh(new RoundedBoxGeometry(0.68, 0.68, 0.05, 3, 0.04), std("#4a7a9b"));
        winFrame.position.set(0.8, 1.45, 1.29);
        g.add(base, roof, doorFrame, door, winFrame, win);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    // pucca pakka house — flat concrete roof, lime-washed walls, blue trim,
    // black water tank, rooftop antenna
    makePakkaHouse(x, z, ry, glow = 0.2, s = 1) {
        const g = new THREE.Group();
        const wallM = new THREE.MeshStandardMaterial({ map: this.plasterTex, roughness: 0.95 });
        const trimM = std("#4a7a9b");
        const base = new THREE.Mesh(new RoundedBoxGeometry(3.4, 2.6, 2.8, 4, 0.14), wallM);
        base.position.y = 1.3;
        base.castShadow = true;
        const slab = new THREE.Mesh(new RoundedBoxGeometry(3.8, 0.2, 3.2, 3, 0.06), std("#b8b0a0"));
        slab.position.y = 2.69;
        slab.castShadow = true;
        const parapet = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.34, 0.14), wallM);
        parapet.position.set(0, 2.92, 1.53);
        const parapetB = parapet.clone();
        parapetB.position.z = -1.53;
        const parapetL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.34, 3.2), wallM);
        parapetL.position.set(-1.83, 2.92, 0);
        const parapetR = parapetL.clone();
        parapetR.position.x = 1.83;
        const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.34, 0.5, 12), std("#1d1b19", { roughness: 0.6 }));
        tank.position.set(-1.1, 3.0, -0.8);
        const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 1.7, 5), std("#8a8a86"));
        mast.position.set(1.2, 3.6, -0.9);
        const crossbar = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.03, 0.03), std("#8a8a86"));
        crossbar.position.set(1.2, 4.2, -0.9);
        const door = new THREE.Mesh(new RoundedBoxGeometry(0.7, 1.3, 0.1, 3, 0.05), trimM);
        door.position.set(0.3, 0.68, 1.43);
        const doorFrame = new THREE.Mesh(new RoundedBoxGeometry(0.9, 1.44, 0.08, 3, 0.04), std("#2f5a7a"));
        doorFrame.position.set(0.3, 0.7, 1.41);
        const winMat = new THREE.MeshBasicMaterial({
            color: new THREE.Color("#3d3831").lerp(new THREE.Color("#ffd489"), glow),
        });
        const win = new THREE.Mesh(new RoundedBoxGeometry(0.52, 0.58, 0.06, 3, 0.04), winMat);
        win.position.set(-0.9, 1.5, 1.41);
        const winFrame = new THREE.Mesh(new RoundedBoxGeometry(0.7, 0.74, 0.05, 3, 0.04), std("#2f5a7a"));
        winFrame.position.set(-0.9, 1.5, 1.39);
        const trim = new THREE.Mesh(new THREE.BoxGeometry(3.44, 0.16, 2.84), trimM);
        trim.position.y = 2.44;
        g.add(base, slab, parapet, parapetB, parapetL, parapetR, tank, mast, crossbar, doorFrame, door, winFrame, win, trim);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    makePalm(x, z, s = 1) {
        const g = new THREE.Group();
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.24, 4.6, 8), std("#9a744e"));
        trunk.position.y = 2.3;
        trunk.rotation.z = 0.08;
        trunk.castShadow = true;
        g.add(trunk);
        for (let i = 0; i < 6; i++) {
            const frond = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 1.7, 4, 6), std("#4a7a3a"));
            const a = (i / 6) * Math.PI * 2;
            frond.scale.z = 0.55;
            frond.position.set(Math.cos(a) * 0.85, 4.55 - (i % 2) * 0.22, Math.sin(a) * 0.85);
            frond.rotation.y = -a;
            frond.rotation.z = -0.6;
            frond.castShadow = true;
            g.add(frond);
        }
        for (let i = 0; i < 3; i++) {
            const nut = new THREE.Mesh(new THREE.SphereGeometry(0.11, 6, 5), std("#6b4f36"));
            nut.position.set(Math.cos(i * 2.1) * 0.22, 4.32, Math.sin(i * 2.1) * 0.22);
            g.add(nut);
        }
        g.position.set(x, groundH(x, z), z);
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    makeMango(x, z, s = 1) {
        const g = new THREE.Group();
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.34, 1.9, 8), new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 }));
        trunk.position.y = 0.95;
        trunk.castShadow = true;
        g.add(trunk);
        const canopyM = std("#3f6e38");
        [
            [0, 2.7, 0, 1.35],
            [0.7, 2.3, 0.35, 0.9],
            [-0.6, 2.4, -0.3, 0.85],
            [0.1, 3.2, -0.2, 0.8],
        ].forEach(([cx, cy, cz, r]) => {
            const blob = new THREE.Mesh(displacedCanopy(r, 2, 0.42), canopyM);
            blob.position.set(cx, cy, cz);
            blob.castShadow = true;
            g.add(blob);
        });
        g.position.set(x, groundH(x, z), z);
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    makeBanana(x, z, s = 1) {
        const g = new THREE.Group();
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.13, 1.6, 6), std("#8fae60"));
        trunk.position.y = 0.8;
        for (let i = 0; i < 4; i++) {
            const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.6, 8, 6), std("#5d8a3a"));
            const a = (i / 4) * Math.PI * 2;
            leaf.scale.set(1.15, 0.16, 0.45);
            leaf.position.set(Math.cos(a) * 0.45, 1.7 - (i % 2) * 0.18, Math.sin(a) * 0.45);
            leaf.rotation.y = -a;
            leaf.rotation.z = -0.3;
            g.add(leaf);
        }
        g.add(trunk);
        g.position.set(x, groundH(x, z), z);
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    makeNeem(x, z, s = 1, bloom = false) {
        const g = new THREE.Group();
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.2, 1.5, 7), new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 }));
        trunk.position.y = 0.75;
        trunk.castShadow = true;
        const canopy = new THREE.Mesh(
            displacedCanopy(1.3, 2, 0.45),
            std(bloom ? "#d98d5f" : "#5d8a4a")
        );
        canopy.position.y = 2.35;
        canopy.castShadow = true;
        g.add(trunk, canopy);
        g.position.set(x, groundH(x, z), z);
        g.scale.setScalar(s);
        this.scene.add(g);
        return g;
    }

    makeCow(x, z, ry = 0, buffalo = false) {
        const g = new THREE.Group();
        const hideM = std(buffalo ? "#4e4a46" : "#ece5da");
        const darkM = std("#3a3630");
        const body = new THREE.Mesh(new RoundedBoxGeometry(1.35, 0.72, 0.62, 3, 0.18), hideM);
        body.position.y = 0.95;
        body.castShadow = true;
        const head = new THREE.Mesh(new RoundedBoxGeometry(0.4, 0.42, 0.38, 3, 0.12), hideM);
        head.position.set(0.88, 1.2, 0);
        const muzzle = new THREE.Mesh(new RoundedBoxGeometry(0.2, 0.2, 0.28, 3, 0.07), std(buffalo ? "#8a8378" : "#d8a8a0"));
        muzzle.position.set(1.1, 1.08, 0);
        [-0.14, 0.14].forEach((hz) => {
            const horn = new THREE.Mesh(new THREE.ConeGeometry(0.05, buffalo ? 0.32 : 0.22, 6), darkM);
            horn.position.set(0.88, 1.52, hz);
            horn.rotation.z = buffalo ? 0.4 : 0;
            g.add(horn);
        });
        [-0.2, 0.2].forEach((lz) => {
            [
                [0.5, lz],
                [-0.5, lz],
            ].forEach(([lx, lz2]) => {
                const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.4, 4, 6), hideM);
                leg.position.set(lx, 0.4, lz2);
                leg.castShadow = true;
                g.add(leg);
            });
        });
        g.add(body, head, muzzle);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeBullockCart(x, z, ry = 0) {
        const g = new THREE.Group();
        const woodM = new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 });
        const darkM = std("#5a4632");
        [-0.7, 0.7].forEach((wx) => {
            const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.09, 8, 16), darkM);
            wheel.position.set(wx, 0.62, 0);
            const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.12, 8), woodM);
            hub.rotation.z = Math.PI / 2;
            hub.position.set(wx, 0.62, 0);
            for (let sp = 0; sp < 4; sp++) {
                const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.08, 0.05), woodM);
                spoke.position.set(wx, 0.62, 0);
                spoke.rotation.z = (sp / 4) * Math.PI;
                g.add(spoke);
            }
            g.add(wheel, hub);
        });
        const cart = new THREE.Mesh(new RoundedBoxGeometry(1.6, 0.5, 1.7, 3, 0.08), woodM);
        cart.position.set(-0.1, 1.05, 0);
        cart.castShadow = true;
        const yoke = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.6, 6), woodM);
        yoke.rotation.z = Math.PI / 2.4;
        yoke.position.set(1.3, 0.72, 0);
        g.add(cart, yoke);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeCharpai(x, z, ry = 0) {
        const g = new THREE.Group();
        const woodM = new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 });
        [
            [-0.85, -0.45],
            [0.85, -0.45],
            [-0.85, 0.45],
            [0.85, 0.45],
        ].forEach(([lx, lz]) => {
            const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.5, 6), woodM);
            leg.position.set(lx, 0.25, lz);
            g.add(leg);
        });
        [-0.45, 0.45].forEach((lz) => {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.09, 0.09), woodM);
            bar.position.set(0, 0.52, lz);
            g.add(bar);
        });
        [-0.85, 0.85].forEach((lx) => {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.09, 1.0), woodM);
            bar.position.set(lx, 0.52, 0);
            g.add(bar);
        });
        const weave = new THREE.Mesh(
            new THREE.PlaneGeometry(1.75, 0.95),
            new THREE.MeshStandardMaterial({ map: this.charpaiTex, roughness: 1, side: THREE.DoubleSide })
        );
        weave.rotation.x = -Math.PI / 2;
        weave.position.y = 0.5;
        g.add(weave);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeBicycle(x, z, ry = 0) {
        const g = new THREE.Group();
        const frameM = std("#2f5a7a");
        const darkM = std("#26241f", { roughness: 0.7 });
        [-0.55, 0.55].forEach((wx) => {
            const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.045, 8, 18), darkM);
            wheel.position.set(wx, 0.36, 0);
            g.add(wheel);
        });
        const frame = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.95, 6), frameM);
        frame.rotation.z = Math.PI / 2.6;
        frame.position.set(0, 0.62, 0);
        const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.9, 6), frameM);
        bar.rotation.z = Math.PI / 2;
        bar.position.set(0, 0.78, 0);
        const seatPost = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.4, 6), frameM);
        seatPost.position.set(-0.3, 0.85, 0);
        const seat = new THREE.Mesh(new RoundedBoxGeometry(0.22, 0.06, 0.12, 2, 0.03), darkM);
        seat.position.set(-0.3, 1.05, 0);
        const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.5, 6), frameM);
        handle.rotation.z = Math.PI / 2;
        handle.position.set(0.42, 1.0, 0);
        const pedalHub = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.1, 8), darkM);
        pedalHub.rotation.z = Math.PI / 2;
        pedalHub.position.set(0, 0.36, 0);
        g.add(frame, bar, seatPost, seat, handle, pedalHub);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeWell(x, z) {
        const g = new THREE.Group();
        const ring = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.08, 0.9, 14, 1, true), std("#b0a89a"));
        ring.position.y = 0.45;
        ring.castShadow = true;
        const water = new THREE.Mesh(new THREE.CircleGeometry(0.86, 14), std("#5f9494", { roughness: 0.2 }));
        water.rotation.x = -Math.PI / 2;
        water.position.y = 0.62;
        const woodM = new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 });
        const postL = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 1.8, 6), woodM);
        postL.position.set(-0.9, 0.9, 0);
        const postR = postL.clone();
        postR.position.x = 0.9;
        const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.1, 6), woodM);
        bar.rotation.z = Math.PI / 2;
        bar.position.y = 1.8;
        const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.8, 4), std("#d9c9a3"));
        rope.position.y = 1.3;
        const bucket = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.13, 0.2, 10), std("#8a7a5f"));
        bucket.position.y = 0.85;
        g.add(ring, water, postL, postR, bar, rope, bucket);
        g.position.set(x, groundH(x, z), z);
        this.scene.add(g);
        return g;
    }

    makeHandPump(x, z) {
        const g = new THREE.Group();
        const block = new THREE.Mesh(new RoundedBoxGeometry(1.0, 0.7, 1.0, 3, 0.08), std("#b8b0a0"));
        block.position.y = 0.35;
        block.castShadow = true;
        const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 1.1, 8), std("#4a6a8a", { metalness: 0.4, roughness: 0.5 }));
        pipe.position.y = 1.2;
        const spout = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.5, 6), std("#4a6a8a", { metalness: 0.4, roughness: 0.5 }));
        spout.rotation.z = Math.PI / 2.3;
        spout.position.set(0.28, 1.45, 0);
        const handle = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.6, 4, 6), std("#4a6a8a", { metalness: 0.4, roughness: 0.5 }));
        handle.position.set(-0.3, 1.62, 0);
        handle.rotation.z = 0.5;
        const wet = new THREE.Mesh(new THREE.CircleGeometry(0.55, 12), std("#6f9d99", { roughness: 0.25, transparent: true, opacity: 0.85 }));
        wet.rotation.x = -Math.PI / 2;
        wet.position.set(0.45, 0.02, 0);
        g.add(block, pipe, spout, handle, wet);
        g.position.set(x, groundH(x, z), z);
        this.scene.add(g);
        return g;
    }

    makeHaystack(x, z, s = 1) {
        const hay = new THREE.Mesh(
            new THREE.ConeGeometry(1.1, 1.7, 10),
            std("#d9b45a")
        );
        hay.position.set(x, 0.85 * s + groundH(x, z), z);
        hay.scale.setScalar(s);
        hay.castShadow = true;
        this.scene.add(hay);
    }

    makeTemple(x, z) {
        const g = new THREE.Group();
        const base = new THREE.Mesh(
            new RoundedBoxGeometry(2.4, 2.2, 2.4, 4, 0.12),
            new THREE.MeshStandardMaterial({ map: this.plasterTex, roughness: 0.95 })
        );
        base.position.y = 1.1;
        base.castShadow = true;
        const shikhara = new THREE.Mesh(
            new THREE.SphereGeometry(1.55, 12, 10, 0, Math.PI * 2, 0, Math.PI / 2),
            new THREE.MeshStandardMaterial({ map: this.clayTex, roughness: 0.9 })
        );
        shikhara.scale.y = 1.35;
        shikhara.position.y = 2.18;
        shikhara.castShadow = true;
        const kalash = new THREE.Mesh(new THREE.SphereGeometry(0.15, 10, 8), std("#d8a83a", { metalness: 0.6, roughness: 0.35 }));
        kalash.position.y = 4.4;
        const door = new THREE.Mesh(new RoundedBoxGeometry(0.58, 1.0, 0.1, 3, 0.05), std("#5a4632"));
        door.position.set(0, 0.5, 1.21);
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 2.6, 6), new THREE.MeshStandardMaterial({ map: this.woodTex }));
        pole.position.set(1.5, 1.3, 1.5);
        const flag = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.35, 0.02), std("#e8842c"));
        flag.position.set(1.78, 2.4, 1.5);
        g.add(base, shikhara, kalash, door, pole, flag);
        g.position.set(x, groundH(x, z), z);
        this.scene.add(g);
        return g;
    }

    makeClothesline(x, z, ry = 0) {
        const g = new THREE.Group();
        const bamboo = std("#c9b45a");
        [-1.6, 1.6].forEach((px) => {
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 2.2, 6), bamboo);
            pole.position.set(px, 1.1, 0);
            g.add(pole);
        });
        const line = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.02, 0.02), std("#8a8a86"));
        line.position.y = 2.0;
        g.add(line);
        const clothColors = ["#c85a32", "#4a7a9b", "#d9c25a", "#7a9e7e"];
        clothColors.forEach((cc, i) => {
            const cloth = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.62, 0.02), std(cc, { side: THREE.DoubleSide }));
            cloth.position.set(-1.1 + i * 0.75, 1.66, 0);
            cloth.rotation.y = (n2(i, x) - 0.5) * 0.5;
            cloth.castShadow = true;
            g.add(cloth);
        });
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeTractor(x, z, ry = 0) {
        const g = new THREE.Group();
        const bodyM = std("#4a7a3a");
        const darkM = std("#2b2927");
        const hood = new THREE.Mesh(new RoundedBoxGeometry(1.5, 0.7, 0.95, 3, 0.1), bodyM);
        hood.position.set(0.65, 0.85, 0);
        hood.castShadow = true;
        const cab = new THREE.Mesh(new RoundedBoxGeometry(0.95, 1.15, 0.95, 3, 0.08), bodyM);
        cab.position.set(-0.5, 1.25, 0);
        cab.castShadow = true;
        const roof = new THREE.Mesh(new RoundedBoxGeometry(1.15, 0.08, 1.15, 2, 0.03), darkM);
        roof.position.set(-0.5, 1.88, 0);
        const exhaust = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 6), darkM);
        exhaust.position.set(0.2, 1.5, -0.35);
        const wheelGeo = new THREE.CylinderGeometry(0.52, 0.52, 0.3, 12);
        const wheelGeoS = new THREE.CylinderGeometry(0.3, 0.3, 0.24, 12);
        [
            [-0.45, 0.52, 0.55, wheelGeo],
            [-0.45, 0.52, -0.55, wheelGeo],
            [0.85, 0.3, 0.5, wheelGeoS],
            [0.85, 0.3, -0.5, wheelGeoS],
        ].forEach(([wx, wy, wz, wg]) => {
            const wheel = new THREE.Mesh(wg, darkM);
            wheel.rotation.x = Math.PI / 2;
            wheel.position.set(wx, wy, wz);
            wheel.castShadow = true;
            g.add(wheel);
        });
        g.add(hood, cab, roof, exhaust);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makePowerPoles(x, zStart, zEnd, count) {
        const poleM = new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 });
        const wireM = std("#3a3a38");
        const zs = [];
        for (let i = 0; i < count; i++) {
            const z = zStart + ((zEnd - zStart) * i) / (count - 1);
            zs.push(z);
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 5.2, 7), poleM);
            pole.position.set(x, 2.6 + groundH(x, z), z);
            pole.castShadow = true;
            const arm = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 1.4), poleM);
            arm.position.set(x, 4.9 + groundH(x, z), z);
            this.scene.add(pole, arm);
        }
        for (let i = 0; i < count - 1; i++) {
            [-0.55, 0, 0.55].forEach((off) => {
                const curve = new THREE.QuadraticBezierCurve3(
                    V(x + off, 4.9 + groundH(x, zs[i]), zs[i]),
                    V(x + off, 4.45 + groundH(x, (zs[i] + zs[i + 1]) / 2), (zs[i] + zs[i + 1]) / 2),
                    V(x + off, 4.9 + groundH(x, zs[i + 1]), zs[i + 1])
                );
                const wire = new THREE.Mesh(new THREE.TubeGeometry(curve, 10, 0.022, 4), wireM);
                this.scene.add(wire);
            });
        }
    }

    makeSign(title, sub, x, z, ry, w = 4.6, h = 1.45) {
        const g = new THREE.Group();
        const boardTex = signTex(title, sub);
        const board = new THREE.Mesh(new RoundedBoxGeometry(w, h, 0.14, 3, 0.06), new THREE.MeshStandardMaterial({ map: this.woodTex, roughness: 0.95 }));
        board.position.y = h / 2 + 0.75;
        board.castShadow = true;
        const face = new THREE.Mesh(
            new THREE.PlaneGeometry(w - 0.12, h - 0.12),
            new THREE.MeshBasicMaterial({ map: boardTex })
        );
        face.position.set(0, h / 2 + 0.75, 0.085);
        const faceBack = new THREE.Mesh(
            new THREE.PlaneGeometry(w - 0.12, h - 0.12),
            new THREE.MeshBasicMaterial({ map: boardTex })
        );
        faceBack.position.set(0, h / 2 + 0.75, -0.085);
        faceBack.rotation.y = Math.PI;
        [-w / 2 + 0.3, w / 2 - 0.3].forEach((px) => {
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, h / 2 + 0.75, 6), new THREE.MeshStandardMaterial({ map: this.woodTex }));
            post.position.set(px, (h / 2 + 0.75) / 2, 0);
            post.castShadow = true;
            g.add(post);
        });
        g.add(board, face, faceBack);
        g.position.set(x, groundH(x, z), z);
        g.rotation.y = ry;
        this.scene.add(g);
        return g;
    }

    makeRangoli(x, z) {
        const g = new THREE.Group();
        [
            [0.62, "#e8842c"],
            [0.42, "#f5f1e6"],
            [0.24, "#c85a32"],
        ].forEach(([r, col]) => {
            const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.05, 6, 20), std(col));
            ring.rotation.x = -Math.PI / 2;
            ring.position.y = 0.03;
            g.add(ring);
        });
        const dot = new THREE.Mesh(new THREE.CircleGeometry(0.1, 10), std("#d9c25a"));
        dot.rotation.x = -Math.PI / 2;
        dot.position.y = 0.035;
        g.add(dot);
        g.position.set(x, 0, z);
        this.scene.add(g);
        return g;
    }

    makeMarigolds(x, y, z, w) {
        const marigoldM = std("#e8842c");
        const rope = new THREE.Mesh(new THREE.BoxGeometry(w, 0.02, 0.02), std("#8a8a86"));
        rope.position.set(x, y, z);
        this.scene.add(rope);
        for (let i = 0; i < 7; i++) {
            const fl = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 5), marigoldM);
            const t = (i + 0.5) / 7;
            fl.position.set(x - w / 2 + w * t, y - 0.12 - Math.sin(t * Math.PI) * 0.22, z);
            this.scene.add(fl);
        }
    }

    /* ---------- chapter 1: the UP village ---------- */

    buildVillage() {
        const hut = this.makeHut(4.2, -8, -0.5, 0.15);
        this.makePakkaHouse(-6.5, -16, 0.6, 0.15, 0.95);
        this.makePakkaHouse(-8.5, -42, 0.4, 0.1, 0.9);
        this.makeHut(7.5, -30, -0.3, 0, 0.85);
        this.makeRangoli(4.05, -6.55);
        this.makeMarigolds(4.2, 1.35, -6.62, 0.9);

        const boy = buildCharacter("#4a7a9b");
        boy.group.position.set(6.4, 0, -4.5);
        boy.group.rotation.y = -2.4;
        boy.parts.armR.rotation.z = -2.3;
        boy.userData = { kind: "wave" };
        this.scene.add(boy.group);
        this.characters.push(boy);

        // mustard + paddy plots
        const plotGeo = new THREE.BoxGeometry(1.9, 0.32, 1.5);
        const plotMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 });
        const cols = [-10, -7.5, 7.5, 10];
        const rows = 30;
        const plots = new THREE.InstancedMesh(plotGeo, plotMat, cols.length * rows);
        const dummy = new THREE.Object3D();
        const plotColors = ["#5e8a52", "#79a05c", "#d9c25a", "#8fb27a"];
        let idx = 0;
        for (let r = 0; r < rows; r++) {
            const pz = -12 - r * 2.2;
            cols.forEach((cx) => {
                dummy.position.set(cx, 0.16, pz);
                dummy.rotation.set(0, (n2(cx, pz) - 0.5) * 0.14, 0);
                dummy.updateMatrix();
                plots.setMatrixAt(idx, dummy.matrix);
                plots.setColorAt(idx, new THREE.Color(plotColors[Math.floor(n2(pz * 1.7, cx) * plotColors.length)]));
                idx++;
            });
        }
        plots.instanceMatrix.needsUpdate = true;
        if (plots.instanceColor) plots.instanceColor.needsUpdate = true;
        plots.castShadow = true;
        this.scene.add(plots);

        // grass tufts
        const tuftGeo = new THREE.ConeGeometry(0.09, 0.34, 4);
        const tuftMat = std("#6f9a58");
        const tufts = new THREE.InstancedMesh(tuftGeo, tuftMat, 160);
        for (let i = 0; i < 160; i++) {
            const tx = -12 + n2(i, 1) * 24;
            const tz = -4 - n2(i, 2) * 50;
            dummy.position.set(tx, 0.17 + groundH(tx, tz) * 0.5, tz);
            dummy.rotation.set(0, n2(i, 4) * 6.28, (n2(i, 5) - 0.5) * 0.3);
            dummy.updateMatrix();
            tufts.setMatrixAt(i, dummy.matrix);
        }
        tufts.instanceMatrix.needsUpdate = true;
        this.scene.add(tufts);

        // sugarcane patch — the UP crop
        const caneGeo = new THREE.CapsuleGeometry(0.045, 1.9, 3, 5);
        const caneMat = std("#a3b45a");
        const canes = new THREE.InstancedMesh(caneGeo, caneMat, 70);
        for (let i = 0; i < 70; i++) {
            dummy.position.set(-14.5 + n2(i, 3) * 3.4, 1.05, -20 - n2(i * 3, 7) * 7);
            dummy.rotation.set(0, 0, (n2(i, 11) - 0.5) * 0.12);
            dummy.updateMatrix();
            canes.setMatrixAt(i, dummy.matrix);
        }
        canes.instanceMatrix.needsUpdate = true;
        canes.castShadow = true;
        this.scene.add(canes);

        // pond with a buffalo
        this.pondMat = std("#6f9d99", { roughness: 0.2, transparent: true, opacity: 0.9 });
        const pond = new THREE.Mesh(new THREE.CircleGeometry(2.6, 18), this.pondMat);
        pond.rotation.x = -Math.PI / 2;
        pond.position.set(-14, 0.05, -33);
        this.scene.add(pond);
        this.makeCow(-11.5, -31, 1.1, true);

        // village life
        this.makePalm(11.5, -5.5, 1.0);
        this.makePalm(-10.5, -12, 0.9);
        this.makePalm(12, -24, 0.85);
        this.makeMango(-13, -26, 1.1);
        this.makeMango(14, -32, 0.95);
        this.makeBanana(-8.7, -11.5, 1.0);
        this.makeBanana(-9.4, -13.2, 0.85);
        this.makeWell(7.6, -19);
        this.makeHandPump(-3, -9.5);
        this.makeTemple(-11, -48);
        this.makeClothesline(1.6, -12.6, 0.15);
        this.makeCharpai(-4.8, -19.5, 0.4);
        this.makeBicycle(-4.4, -13.8, 1.1);
        this.makeTractor(13.5, -40, -0.6);
        this.makeBullockCart(15.5, -56, 0.5);
        this.makeHaystack(16.5, -50, 1.1);
        this.makeHaystack(18.5, -53, 0.85);
        this.makeHaystack(-16, -38, 0.95);
        this.makeCow(10.8, -14.5, -1.2);
        this.makePowerPoles(-9.5, -18, -60, 3);
        this.makeSign("A VILLAGE IN UTTAR PRADESH", "Chapter one · where it begins", 9.6, -2.2, -2.35, 5.4);

        // stone path
        const stoneGeo = new THREE.CircleGeometry(0.5, 7);
        const stoneMat = std("#cfc0a0");
        for (let i = 0; i < 8; i++) {
            const stone = new THREE.Mesh(stoneGeo, stoneMat);
            stone.rotation.x = -Math.PI / 2;
            stone.position.set(4.6 - i * 0.14, 0.03, -1.2 - i * 1.1);
            this.scene.add(stone);
        }

        // birds circling over the fields
        for (let i = 0; i < 5; i++) {
            const bird = new THREE.Group();
            const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.05, 0.22, 3, 5), std("#4a4540"));
            body.rotation.z = Math.PI / 2;
            const wingL = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.02, 0.14), std("#4a4540"));
            wingL.position.set(0, 0.03, 0.22);
            const wingR = wingL.clone();
            wingR.position.z = -0.22;
            bird.add(body, wingL, wingR);
            bird.userData = { r: 14 + n2(i, 2) * 12, a: n2(i, 5) * 6.28, speed: 0.25 + n2(i, 7) * 0.15, h: 11 + n2(i, 9) * 4, wl: wingL, wr: wingR, cx: 0, cz: -28 };
            this.birds.push(bird);
            this.scene.add(bird);
        }

        this.scatterTrees([
            { z0: -58, z1: -92, count: 14, kinds: ["neem", "palm", "mango"] },
            { z0: -216, z1: -252, count: 8, kinds: ["bare"] },
            { z0: -256, z1: -300, count: 16, kinds: ["neem", "bloom", "mango"] },
        ]);

        this.registerHotspot(hut, "village", SCENES[0].p);
    }

    scatterTrees(bands) {
        bands.forEach((b, bi) => {
            for (let i = 0; i < b.count; i++) {
                const side = n2(i * 3.7 + bi, b.z0) > 0.5 ? 1 : -1;
                const x = side * (14 + n2(i, b.z1) * 20);
                const z = b.z0 - n2(i * 1.3, b.z0) * (b.z0 - b.z1);
                const s = 0.8 + n2(i * 7.1, b.z0) * 0.6;
                const kind = b.kinds[Math.floor(n2(i * 2.9, b.z1) * b.kinds.length)];
                if (kind === "bare") {
                    const tree = this.makeNeem(x, z, s);
                    tree.children[1].material = std("#6e6a60");
                } else this.makeNeem(x, z, s, kind === "bloom");
            }
        });
    }

    /* ---------- chapter 2: SPV — pucca village school ---------- */

    buildSchool() {
        const g = new THREE.Group();
        const wallM = new THREE.MeshStandardMaterial({ map: this.plasterTex, roughness: 0.95 });
        const trimM = std("#2f5a7a");
        const platform = new THREE.Mesh(new RoundedBoxGeometry(10, 0.45, 6.5, 3, 0.1), std("#cfc0a0"));
        platform.position.y = 0.22;
        platform.receiveShadow = true;

        const main = new THREE.Mesh(new RoundedBoxGeometry(8, 3.2, 4.4, 4, 0.16), wallM);
        main.position.set(0, 2.05, -1.2);
        main.castShadow = true;
        const slab = new THREE.Mesh(new RoundedBoxGeometry(8.5, 0.2, 4.9, 3, 0.06), std("#b8b0a0"));
        slab.position.set(0, 3.75, -1.2);
        slab.castShadow = true;
        const parapet = new THREE.Mesh(new THREE.BoxGeometry(8.5, 0.36, 0.16), wallM);
        parapet.position.set(0, 4.0, -3.55);
        const parapetF = parapet.clone();
        parapetF.position.z = 1.15;
        [-4.17, 4.17].forEach((px) => {
            const side = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.36, 4.9), wallM);
            side.position.set(px, 4.0, -1.2);
            g.add(side);
        });
        const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.38, 0.55, 12), std("#1d1b19", { roughness: 0.6 }));
        tank.position.set(-2.6, 4.1, -2.2);
        const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 1.8, 5), std("#8a8a86"));
        mast.position.set(2.8, 4.7, -2.4);
        const trim = new THREE.Mesh(new THREE.BoxGeometry(8.04, 0.2, 4.44), trimM);
        trim.position.y = 3.32;

        const verandaSlab = new THREE.Mesh(new RoundedBoxGeometry(8, 0.16, 1.8, 3, 0.05), std("#cfc0a0"));
        verandaSlab.position.set(0, 0.5, 2.1);
        [-3.4, -1.15, 1.15, 3.4].forEach((px) => {
            const col = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 2.6, 8), trimM);
            col.position.set(px, 1.8, 2.1);
            col.castShadow = true;
            g.add(col);
        });

        const winMat = new THREE.MeshBasicMaterial({ color: "#2b3d46" });
        [-2.6, 0, 2.6].forEach((wx) => {
            const win = new THREE.Mesh(new RoundedBoxGeometry(1.15, 1.0, 0.1, 3, 0.04), winMat);
            win.position.set(wx, 2.1, 1.02);
            const frame = new THREE.Mesh(new RoundedBoxGeometry(1.35, 1.2, 0.08, 3, 0.04), trimM);
            frame.position.set(wx, 2.1, 1.0);
            g.add(win, frame);
        });
        const door = new THREE.Mesh(new RoundedBoxGeometry(0.9, 1.7, 0.1, 3, 0.05), std("#4a7a9b"));
        door.position.set(-4.05, 1.3, 0.4);
        door.rotation.y = Math.PI / 2;

        const board = new THREE.Mesh(new RoundedBoxGeometry(2.2, 1.1, 0.08, 3, 0.04), std("#2f4a3a"));
        board.position.set(0.6, 2.0, 1.16);
        const chalkM = new THREE.MeshBasicMaterial({ color: "#e8e4d8" });
        [
            [0.15, 2.1, 0.5],
            [0.7, 1.95, 0.8],
            [1.05, 2.25, 0.35],
        ].forEach(([cx, cy, len]) => {
            const chalk = new THREE.Mesh(new THREE.BoxGeometry(len, 0.045, 0.02), chalkM);
            chalk.position.set(cx, cy, 1.11);
            chalk.rotation.z = (n2(cx, cy) - 0.5) * 0.2;
            g.add(chalk);
        });

        const deskTop = new THREE.Mesh(new RoundedBoxGeometry(1.05, 0.08, 0.65, 3, 0.03), new THREE.MeshStandardMaterial({ map: this.woodTex }));
        deskTop.position.set(0.6, 0.86, 1.7);
        deskTop.castShadow = true;
        const deskLeg = new THREE.Mesh(new RoundedBoxGeometry(0.8, 0.72, 0.5, 3, 0.05), std("#7d6248"));
        deskLeg.position.set(0.6, 0.5, 1.7);
        const paper = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.02, 0.28), new THREE.MeshBasicMaterial({ color: "#f5f1e6" }));
        paper.position.set(0.5, 0.91, 1.72);
        paper.rotation.y = 0.2;
        const book = new THREE.Mesh(new RoundedBoxGeometry(0.32, 0.07, 0.24, 2, 0.03), std("#c85a32"));
        book.position.set(0.82, 0.93, 1.68);
        book.rotation.y = -0.3;

        const boy = buildCharacter("#c85a32");
        boy.group.position.set(0.6, 0.44, 2.35);
        boy.group.rotation.y = Math.PI;
        boy.parts.legL.rotation.x = -1.3;
        boy.parts.legR.rotation.x = -1.3;
        boy.parts.armL.rotation.x = -1.1;
        boy.parts.armR.rotation.x = -1.1;
        boy.userData = { kind: "write" };
        this.scene.add(boy.group);
        this.characters.push(boy);

        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 4.6, 6), std("#8a8a86"));
        pole.position.set(3.9, 2.3, 2.9);
        const flag = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 0.03), std("#c85a32"));
        flag.position.set(4.35, 4.25, 2.9);

        g.add(platform, main, slab, parapet, parapetF, tank, mast, trim, verandaSlab, door, board, deskTop, deskLeg, paper, book, pole, flag);
        g.position.set(-3, 0, -75);
        this.scene.add(g);

        this.makeSign("SPV PUBLIC SCHOOL", "Chapter two · school days", -3, -70.4, 0, 5.6);
        this.registerHotspot(g, "school", SCENES[1].p);
    }

    /* ---------- chapter 3: airfield + flight + map ---------- */

    buildAirfield() {
        const strip = new THREE.Mesh(
            new THREE.BoxGeometry(6, 0.22, 34),
            new THREE.MeshStandardMaterial({ map: this.concreteTex, roughness: 0.95 })
        );
        strip.position.set(0, 0.11, -112);
        strip.receiveShadow = true;
        this.scene.add(strip);
        const dashGeo = new THREE.BoxGeometry(0.35, 0.02, 2.2);
        const dashMat = new THREE.MeshBasicMaterial({ color: "#efece2" });
        const dashes = new THREE.InstancedMesh(dashGeo, dashMat, 12);
        const dummy = new THREE.Object3D();
        for (let i = 0; i < 12; i++) {
            dummy.position.set(0, 0.23, -97 - i * 2.6);
            dummy.updateMatrix();
            dashes.setMatrixAt(i, dummy.matrix);
        }
        dashes.instanceMatrix.needsUpdate = true;
        this.scene.add(dashes);

        const hangar = new THREE.Mesh(
            new RoundedBoxGeometry(5, 2.6, 4, 3, 0.15),
            new THREE.MeshStandardMaterial({ map: this.concreteTex, roughness: 0.95 })
        );
        hangar.position.set(-8, 1.3, -104);
        hangar.castShadow = true;
        const hangarRoof = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 2.5, 5, 12, 1, false, 0, Math.PI), std("#8a7a5f"));
        hangarRoof.rotation.z = Math.PI / 2;
        hangarRoof.rotation.y = Math.PI / 2;
        hangarRoof.position.set(-8, 2.6, -104);
        this.scene.add(hangar, hangarRoof);

        const wpole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 3.2, 6), std("#8a8a86"));
        wpole.position.set(4.6, 1.6, -98);
        const sock = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.9, 8), std("#e8842c"));
        sock.rotation.z = Math.PI / 2;
        sock.position.set(5.15, 3.1, -98);
        this.scene.add(wpole, sock);

        this.makeSign("UP → BENGALURU", "Chapter three · the flight", 5.6, -100.5, -0.4, 4.4);

        // ---- accurate India diorama map (Survey of India extent, GeoJSON-derived) ----
        const map = new THREE.Group();
        const toShape = (ring) => {
            const shape = new THREE.Shape();
            ring.forEach(([lon, lat], i) => {
                const px = (lon - 81.2) * 0.62;
                const py = (lat - 22.5) * 0.62;
                if (i === 0) shape.moveTo(px, py);
                else shape.lineTo(px, py);
            });
            shape.closePath();
            return shape;
        };
        const shapes = [toShape(INDIA_GEO.main), ...INDIA_GEO.islands.map(toShape)];
        const faceM = std("#d9c9a3", { roughness: 0.85 });
        const sideM = std("#b0552d", { roughness: 0.9 });
        shapes.forEach((sh) => {
            const geo = new THREE.ExtrudeGeometry(sh, { depth: 0.35, bevelEnabled: false });
            geo.translate(0, 0, -0.175);
            const body = new THREE.Mesh(geo, [faceM, sideM]);
            map.add(body);
        });

        const dotV = V((80.3 - 81.2) * 0.62, (26.4 - 22.5) * 0.62, 0.3);
        const dotB = V((77.59 - 81.2) * 0.62, (12.97 - 22.5) * 0.62, 0.3);
        [dotV, dotB].forEach((d, i) => {
            const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.16, 12), std(i === 0 ? "#3a5a40" : "#c85a32"));
            dot.position.copy(d);
            map.add(dot);
        });
        const mapLabelTex = (txt) =>
            canvasTexture(256, 96, (ctx, w, h) => {
                ctx.fillStyle = "rgba(43,41,39,0.88)";
                ctx.beginPath();
                ctx.roundRect(0, 0, w, h, 24);
                ctx.fill();
                ctx.fillStyle = "#f6f1e3";
                ctx.font = "bold 42px Georgia, serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(txt, w / 2, h / 2 + 2);
            });
        [
            ["UP · HOME", dotV, 0, 1.5],
            ["BENGALURU · CU", dotB, 0, -1.7],
        ].forEach(([txt, d, ox, oy]) => {
            const lab = new THREE.Mesh(
                new THREE.PlaneGeometry(3.0, 1.12),
                new THREE.MeshBasicMaterial({ map: mapLabelTex(txt), transparent: true })
            );
            lab.position.set(d.x + ox, d.y + oy, 0.34);
            map.add(lab);
        });
        const routeCurve = new THREE.QuadraticBezierCurve3(
            dotV.clone(),
            dotV.clone().add(dotB.clone().sub(dotV).multiplyScalar(0.5)).add(V(4.5, 0, 0)),
            dotB.clone()
        );
        const routePts = routeCurve.getPoints(60);
        const routeGeo = new THREE.BufferGeometry().setFromPoints(routePts);
        const route = new THREE.Line(routeGeo, new THREE.LineDashedMaterial({
            color: "#c85a32", dashSize: 0.4, gapSize: 0.25,
        }));
        route.computeLineDistances();
        map.add(route);

        const marker = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.6, 8), std("#2f5a7a"));
        map.add(marker);
        this.mapMarker = marker;
        this.mapRoute = routeCurve;
        this.map = map;
        map.position.set(14, 10.5, -131);
        map.rotation.y = -1.15;
        map.rotation.x = -0.18;
        this.scene.add(map);

        // ---- the aeroplane ----
        const plane = new THREE.Group();
        const bodyM = std("#f2efe6");
        const accentM = std("#c85a32");
        const darkM = std("#2b2927");
        const fuselage = new THREE.Mesh(new THREE.CapsuleGeometry(0.5, 3.4, 6, 12), bodyM);
        fuselage.rotation.x = Math.PI / 2;
        fuselage.position.y = 0.95;
        fuselage.castShadow = true;
        const nose = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 10), accentM);
        nose.scale.set(1, 1, 1.6);
        nose.position.set(0, 0.95, -2.2);
        const wing = new THREE.Mesh(new RoundedBoxGeometry(6.4, 0.14, 1.25, 3, 0.05), bodyM);
        wing.position.set(0, 0.85, -0.3);
        wing.castShadow = true;
        const tailFin = new THREE.Mesh(new RoundedBoxGeometry(0.14, 1.15, 1.0, 3, 0.05), accentM);
        tailFin.position.set(0, 1.7, 2.0);
        const tailWing = new THREE.Mesh(new RoundedBoxGeometry(2.3, 0.12, 0.75, 3, 0.04), bodyM);
        tailWing.position.set(0, 1.2, 2.1);
        const cockpit = new THREE.Mesh(
            new THREE.SphereGeometry(0.4, 10, 8),
            new THREE.MeshStandardMaterial({ color: "#9fc4d2", transparent: true, opacity: 0.55, roughness: 0.15 })
        );
        cockpit.scale.set(1.1, 0.8, 1.3);
        cockpit.position.set(0, 1.22, -1.3);
        const engL = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.9, 10), darkM);
        engL.rotation.x = Math.PI / 2;
        engL.position.set(-1.7, 0.62, -0.5);
        const engR = engL.clone();
        engR.position.x = 1.7;
        plane.add(fuselage, nose, wing, tailFin, tailWing, cockpit, engL, engR);

        const boy = buildCharacter("#3a5a40");
        boy.group.position.set(0, 0.3, -1.1);
        boy.group.scale.setScalar(0.62);
        boy.parts.legL.rotation.x = -1.2;
        boy.parts.legR.rotation.x = -1.2;
        boy.parts.armL.rotation.x = -0.9;
        boy.parts.armR.rotation.x = -0.9;
        plane.add(boy.group);
        this.characters.push(boy);

        this.plane = plane;
        this.scene.add(plane);
        this.registerHotspot(plane, "flight", SCENES[2].p);
    }

    /* ---------- chapter 4: Bengaluru + CU college ---------- */

    buildCity() {
        const towers = [
            { x: -15, h: 11, w: 3.4, tex: this.glassTex, z: -176 },
            { x: -10.5, h: 15, w: 4.2, tex: this.glassTex, z: -184 },
            { x: -17, h: 7.5, w: 3, tex: this.glassTex, z: -192 },
            { x: 8, h: 13, w: 4, tex: this.glassTex, z: -180 },
            { x: 15, h: 9, w: 3.4, tex: this.glassTex, z: -188 },
            { x: 20, h: 6.5, w: 3, tex: this.glassTex, z: -174 },
        ];
        const winGeo = new THREE.BoxGeometry(0.5, 0.7, 0.1);
        const winMat = new THREE.MeshBasicMaterial({ color: "#ffe1a0" });
        const winSpots = [];
        towers.forEach((t) => {
            const mesh = new THREE.Mesh(
                new RoundedBoxGeometry(t.w, t.h, t.w, 4, 0.3),
                new THREE.MeshStandardMaterial({ map: t.tex, roughness: 0.6, metalness: 0.1 })
            );
            mesh.position.set(t.x, t.h / 2, t.z);
            mesh.castShadow = true;
            this.scene.add(mesh);
            const colsN = Math.max(2, Math.floor(t.w / 1.2));
            const rowsN = Math.max(2, Math.floor((t.h - 2) / 1.9));
            for (let r = 0; r < rowsN; r++) {
                for (let cI = 0; cI < colsN; cI++) {
                    winSpots.push([
                        t.x - ((colsN - 1) * 1.2) / 2 + cI * 1.2,
                        2 + r * 1.9,
                        t.z + t.w / 2 + 0.06,
                    ]);
                }
            }
        });
        const wins = new THREE.InstancedMesh(winGeo, winMat, winSpots.length);
        const dummy = new THREE.Object3D();
        winSpots.forEach(([wx, wy, wz], i) => {
            dummy.position.set(wx, wy, wz);
            dummy.updateMatrix();
            wins.setMatrixAt(i, dummy.matrix);
        });
        wins.instanceMatrix.needsUpdate = true;
        this.scene.add(wins);

        // CU — the college
        const g = new THREE.Group();
        const main = new THREE.Mesh(
            new RoundedBoxGeometry(9, 4.6, 4.2, 4, 0.2),
            new THREE.MeshStandardMaterial({ map: this.plasterTex, roughness: 0.95 })
        );
        main.position.y = 2.3;
        main.castShadow = true;
        const roofSlab = new THREE.Mesh(new RoundedBoxGeometry(9.6, 0.4, 4.8, 3, 0.08), std("#2f5a7a"));
        roofSlab.position.y = 4.8;
        const pillars = std("#c9bd93");
        [-3.4, -1.2, 1.2, 3.4].forEach((px) => {
            const col = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.32, 3.4, 10), pillars);
            col.position.set(px, 1.7, 2.35);
            col.castShadow = true;
            g.add(col);
        });
        const lintel = new THREE.Mesh(new RoundedBoxGeometry(8.6, 0.5, 0.6, 3, 0.06), std("#2f5a7a"));
        lintel.position.set(0, 3.7, 2.35);
        const clockFace = new THREE.Mesh(new THREE.CircleGeometry(0.75, 16), new THREE.MeshBasicMaterial({ color: "#f5f1e6" }));
        clockFace.position.set(0, 5.6, 2.2);
        const handV = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.5, 0.02), new THREE.MeshBasicMaterial({ color: "#2b2927" }));
        handV.position.set(0, 5.68, 2.24);
        const handH = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.07, 0.02), new THREE.MeshBasicMaterial({ color: "#2b2927" }));
        handH.position.set(0.12, 5.6, 2.24);
        g.add(main, roofSlab, lintel, clockFace, handV, handH);

        const path = new THREE.Mesh(
            new THREE.BoxGeometry(2.4, 0.1, 14),
            new THREE.MeshStandardMaterial({ map: this.dirtTex, roughness: 1 })
        );
        path.position.set(0, 0.05, 10);
        path.receiveShadow = true;
        g.add(path);

        this.makeNeem(-6.5, -202, 1.0);
        this.makeNeem(6.5, -201, 0.9);

        const boy = buildCharacter("#e6dbc9");
        boy.group.position.set(0.6, 0, -196.5);
        boy.group.rotation.y = Math.PI * 0.9;
        boy.userData = { kind: "walk" };
        this.scene.add(boy.group);
        this.characters.push(boy);

        g.position.set(0, 0, -206);
        this.scene.add(g);

        this.makeSign("CU · BENGALURU", "Chapter four · a new world", 0, -200.6, 0, 5.8);
        this.registerHotspot(g, "college", SCENES[3].p);
    }

    /* ---------- chapter 5: the return ---------- */

    buildStormRoad() {
        const road = new THREE.Mesh(
            new THREE.BoxGeometry(3.4, 0.14, 46),
            new THREE.MeshStandardMaterial({ map: this.dirtTex, roughness: 1 })
        );
        road.position.set(0, 0.07, -233);
        road.receiveShadow = true;
        this.scene.add(road);

        const puddleM = new THREE.MeshStandardMaterial({ color: "#55606a", roughness: 0.15, metalness: 0.3, transparent: true, opacity: 0.85 });
        [
            [1.9, -224],
            [-1.7, -231],
            [2.1, -240],
            [-2.0, -247],
        ].forEach(([px, pz]) => {
            const puddle = new THREE.Mesh(new THREE.CircleGeometry(0.55 + Math.random() * 0.3, 12), puddleM);
            puddle.rotation.x = -Math.PI / 2;
            puddle.position.set(px, 0.15, pz);
            this.scene.add(puddle);
        });

        [
            [2.2, -220],
            [2.2, -232],
        ].forEach(([mx, mz]) => {
            const stone = new THREE.Mesh(new RoundedBoxGeometry(0.3, 0.55, 0.14, 2, 0.05), std("#e6dbc9"));
            stone.position.set(mx, 0.27, mz);
            const top = new THREE.Mesh(new RoundedBoxGeometry(0.32, 0.18, 0.16, 2, 0.05), std("#c85a32"));
            top.position.set(mx, 0.55, mz);
            this.scene.add(stone, top);
        });

        this.makePakkaHouse(-4, -251, 0.5, 0.35, 0.9);
        this.makePowerPoles(7.6, -222, -250, 3);

        const boy = buildCharacter("#c85a32", true);
        boy.group.position.set(0.4, 0, -236);
        boy.group.rotation.y = Math.PI * 1.05;
        boy.parts.torso.rotation.x = 0.18;
        boy.userData = { kind: "walk" };
        this.scene.add(boy.group);
        this.characters.push(boy);

        this.makeSign("HOME, 2 KM", "Chapter five · the call home", -2.6, -223, 0.35, 4.0);
    }

    /* ---------- chapter 6: dawn, rebuilding ---------- */

    buildDawnHome() {
        const hut = this.makePakkaHouse(5, -281, -0.6, 1);

        const boy = buildCharacter("#3a5a40");
        boy.group.position.set(4.0, -0.16, -279.2);
        boy.group.rotation.y = -0.9;
        boy.parts.legL.rotation.x = -1.35;
        boy.parts.legR.rotation.x = -1.35;
        boy.parts.armL.rotation.x = -1.2;
        boy.parts.armR.rotation.x = -1.2;
        boy.userData = { kind: "type" };
        this.scene.add(boy.group);
        this.characters.push(boy);

        const laptopBase = new THREE.Mesh(new RoundedBoxGeometry(0.56, 0.05, 0.4, 2, 0.02), std("#2b2927"));
        laptopBase.position.set(3.65, 0.6, -280.0);
        laptopBase.rotation.y = -0.9;
        const screenMat = new THREE.MeshBasicMaterial({ color: "#ffe9b0" });
        const laptopScreen = new THREE.Mesh(new RoundedBoxGeometry(0.56, 0.4, 0.03, 2, 0.015), std("#2b2927"));
        laptopScreen.position.set(3.47, 0.8, -280.28);
        laptopScreen.rotation.y = -0.9;
        laptopScreen.rotation.x = 0.25;
        const glow = new THREE.Mesh(new THREE.PlaneGeometry(0.48, 0.32), screenMat);
        glow.position.set(3.47, 0.8, -280.26);
        glow.rotation.y = -0.9;
        glow.rotation.x = 0.25;
        const light = new THREE.PointLight(0xffc46b, 1.6, 7);
        light.position.set(3.6, 1.1, -279.6);
        const step = new THREE.Mesh(new RoundedBoxGeometry(1.2, 0.42, 0.95, 3, 0.06), std("#cfc0a0"));
        step.position.set(4.0, 0.21, -279.3);
        step.castShadow = true;
        this.scene.add(laptopBase, laptopScreen, glow, light, step);

        this.makeNeem(9, -286, 1.1, true);
        this.makeSign("HOME AGAIN", "Chapter six · rebuilding", 6.8, -277, -0.8, 4.2);

        this.sunSphereMat = new THREE.MeshBasicMaterial({ color: "#ffc97e", transparent: true, opacity: 0 });
        const sunSphere = new THREE.Mesh(new THREE.SphereGeometry(9, 20, 14), this.sunSphereMat);
        sunSphere.position.set(14, 9, -318);
        this.haloMat = new THREE.MeshBasicMaterial({ color: "#ffca7a", transparent: true, opacity: 0 });
        const halo = new THREE.Mesh(new THREE.SphereGeometry(14, 20, 14), this.haloMat);
        halo.position.copy(sunSphere.position);
        this.sunSphere = sunSphere;
        this.scene.add(sunSphere, halo);

        this.registerHotspot(hut, "dawn", SCENES[5].p);
    }

    /* ---------- chapter 7: floating project gallery ---------- */

    buildProjects() {
        const defs = [
            {
                title: "CRAFTEDSPACE.SHOP",
                desc: "A full-stack website — designed, built and shipped by me",
                pos: [-7.2, 4.2, -336, 0.42],
                link: LINKS.craftedspaces,
            },
            {
                title: "FIVERR · FREELANCING",
                desc: "Full-stack development gigs — hire me there",
                pos: [-3.6, 5.0, -339.5, 0.2],
                link: LINKS.fiverr,
            },
            {
                title: "PACEPIVOT",
                desc: "Internal project — Amazon FBA operations",
                pos: [0, 4.4, -341, 0],
                link: LINKS.pacepivot,
            },
            {
                title: "ASTRON-1",
                desc: "My AI experiment — learning and building with AI",
                pos: [3.6, 5.0, -339.5, -0.2],
            },
            {
                title: "LOADING...",
                desc: "the next project — under construction",
                pos: [7.2, 4.2, -336, -0.42],
                loading: true,
            },
        ];
        defs.forEach((d, i) => {
            const g = new THREE.Group();
            const back = new THREE.Mesh(
                new RoundedBoxGeometry(3.9, 2.5, 0.16, 3, 0.1),
                std("#2b2927", { roughness: 0.4, metalness: 0.2 })
            );
            const face = new THREE.Mesh(
                new THREE.PlaneGeometry(3.6, 2.24),
                new THREE.MeshBasicMaterial({ map: panelTex(d.title, d.desc) })
            );
            face.position.z = 0.09;
            const edge = new THREE.Mesh(
                new THREE.BoxGeometry(3.98, 0.09, 0.2),
                new THREE.MeshBasicMaterial({ color: "#c85a32" })
            );
            edge.position.y = 1.29;
            g.add(back, face, edge);

            if (d.loading) {
                const ring = new THREE.Mesh(
                    new THREE.TorusGeometry(0.26, 0.05, 8, 22),
                    new THREE.MeshBasicMaterial({ color: "#c85a32" })
                );
                ring.position.set(0, -0.85, 0.15);
                g.add(ring);
                this.loaderRing = ring;
            }

            const [px, py, pz, pr] = d.pos;
            g.position.set(px, py, pz);
            g.rotation.y = pr;
            g.userData.baseY = py;
            g.userData.projectPanel = true;
            this.panels.push(g);
            this.scene.add(g);
            this.registerHotspot(g, `project-${i}`, null);
            if (d.link) g.userData.link = d.link;
        });

        this.makeSign("THE WORK", "Chapter seven · recent builds", 8.5, -331, -0.9, 4.2, 1.3);

        this.starCount = 350;
        const starPos = new Float32Array(this.starCount * 3);
        for (let i = 0; i < this.starCount; i++) {
            starPos[i * 3] = -60 + Math.random() * 120;
            starPos[i * 3 + 1] = 6 + Math.random() * 44;
            starPos[i * 3 + 2] = -300 - Math.random() * 90;
        }
        const starGeo = new THREE.BufferGeometry();
        starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
        this.starMat = new THREE.PointsMaterial({
            color: 0xfff6e0,
            size: 0.4,
            transparent: true,
            opacity: 0,
            depthWrite: false,
        });
        this.stars = new THREE.Points(starGeo, this.starMat);
        this.scene.add(this.stars);
    }

    /* ---------- clouds & particles ---------- */

    buildClouds() {
        this.clouds = [];
        this.cloudMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 });
        const puffGeo = new THREE.SphereGeometry(1, 10, 8);
        const mkCloud = (x, y, z, s) => {
            const cloud = new THREE.Group();
            const n = 3 + Math.floor(n2(x, z) * 3);
            for (let j = 0; j < n; j++) {
                const puff = new THREE.Mesh(puffGeo, this.cloudMat);
                puff.position.set(j * 1.7 - n * 0.7, n2(j * 13.7 + x) * 0.6, n2(j, z) * 1.2);
                puff.scale.set(1.7 + n2(j * 3, x) * 1.3, 0.75, 1.2);
                cloud.add(puff);
            }
            cloud.position.set(x, y, z);
            cloud.scale.setScalar(s);
            this.clouds.push(cloud);
            this.scene.add(cloud);
        };
        for (let i = 0; i < 8; i++) mkCloud(-45 + n2(i, 1) * 90, 16 + n2(i, 2) * 6, 5 - n2(i, 3) * 90, 1.4);
        for (let i = 0; i < 7; i++) mkCloud(-35 + n2(i, 5) * 70, 11 + n2(i, 6) * 10, -110 - n2(i, 7) * 60, 1.8);
        for (let i = 0; i < 5; i++)
            mkCloud((n2(i, 4) > 0.5 ? 1 : -1) * (16 + n2(i, 9) * 30), 18 + n2(i, 10) * 6, -210 - n2(i, 11) * 90, 1.3);
    }

    buildParticles() {
        this.rainCount = 800;
        const rainPos = new Float32Array(this.rainCount * 3);
        for (let i = 0; i < this.rainCount; i++) {
            rainPos[i * 3] = -24 + Math.random() * 48;
            rainPos[i * 3 + 1] = Math.random() * 26;
            rainPos[i * 3 + 2] = -214 - Math.random() * 44;
        }
        const rainGeo = new THREE.BufferGeometry();
        rainGeo.setAttribute("position", new THREE.BufferAttribute(rainPos, 3));
        this.rainMat = new THREE.PointsMaterial({
            color: 0xb9c2c4,
            size: 0.13,
            transparent: true,
            opacity: 0,
            depthWrite: false,
        });
        this.rain = new THREE.Points(rainGeo, this.rainMat);
        this.scene.add(this.rain);

        this.dustCount = 220;
        const dustPos = new Float32Array(this.dustCount * 3);
        for (let i = 0; i < this.dustCount; i++) {
            dustPos[i * 3] = -26 + Math.random() * 52;
            dustPos[i * 3 + 1] = 0.3 + Math.random() * 8.7;
            dustPos[i * 3 + 2] = 12 - Math.random() * 100;
        }
        const dustGeo = new THREE.BufferGeometry();
        dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
        this.dustMat = new THREE.PointsMaterial({
            color: 0xe8d3a0,
            size: 0.15,
            transparent: true,
            opacity: 0,
            depthWrite: false,
        });
        this.dust = new THREE.Points(dustGeo, this.dustMat);
        this.scene.add(this.dust);
    }

    /* ---------- explore mode ---------- */

    toggleExplore() {
        if (this.explore.active) this.exitExplore();
        else this.enterExplore();
    }

    enterExplore() {
        if (this.explore.active) return;
        this.explore.active = true;
        this.explore.blend = 0;
        const e = new THREE.Euler().setFromQuaternion(this.camera.quaternion, "YXZ");
        this.explore.yaw = e.y;
        this.explore.pitch = e.x;
        scrollState.lenis?.stop();
        this.opts.onExploreChange?.(true);
    }

    exitExplore() {
        if (!this.explore.active) return;
        this.explore.active = false;
        this.explore.blend = 0;
        this.explore.fromPos.copy(this.camera.position);
        scrollState.lenis?.start();
        document.exitPointerLock?.();
        this.opts.onExploreChange?.(false);
    }

    setFly(dir) {
        this.flyInput = dir;
    }

    scrollToProgress(p) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollState.lenis) scrollState.lenis.scrollTo(p * max, { duration: 3.4 });
        else window.scrollTo({ top: p * max, behavior: "smooth" });
    }

    handleHover(e) {
        this.tmpPointer = this.tmpPointer || new THREE.Vector2();
        this.tmpPointer.x = this.pointer.x;
        this.tmpPointer.y = -this.pointer.y;
        this.raycaster.setFromCamera(this.tmpPointer, this.camera);
        const hits = this.raycaster.intersectObjects(this.hotspots, true);
        const hit = hits.length ? hits[0].object.parent : null;
        const root = hit ? this.hotspots.find((h) => h === hit || this.isChildOf(hit, h)) : null;
        this.hovered = root || null;
        document.body.style.cursor = root ? "pointer" : "";
    }

    isChildOf(node, parent) {
        let n = node;
        while (n) {
            if (n === parent) return true;
            n = n.parent;
        }
        return false;
    }

    /* ---------- runtime ---------- */

    resize() {
        const w = this.container.clientWidth || window.innerWidth;
        const h = this.container.clientHeight || window.innerHeight;
        this.camera.aspect = w / h;
        this.camera.fov = w / h < 0.8 ? 72 : 55;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h);
    }

    updateExplore(dt) {
        const ex = this.explore;
        if (!ex.active) {
            if (ex.blend < 1) {
                ex.blend = Math.min(1, ex.blend + dt * 1.2);
                const ease = 1 - Math.pow(1 - ex.blend, 3);
                this.camCurve.getPoint(THREE.MathUtils.clamp(this.progress, 0, 1), this.tmpA);
                this.lookCurve.getPoint(Math.min(1, this.progress + 0.012), this.tmpB);
                this.camera.position.lerpVectors(ex.fromPos, this.tmpA, ease);
                this.camera.lookAt(this.tmpB);
            }
            return;
        }

        const k = ex.keys;
        const fwd = (k.KeyW || k.ArrowUp ? 1 : 0) - (k.KeyS || k.ArrowDown ? 1 : 0) + this.flyInput;
        const right = (k.KeyD || k.ArrowRight ? 1 : 0) - (k.KeyA || k.ArrowLeft ? 1 : 0);
        const up = (k.Space ? 1 : 0) - (k.KeyC || k.ControlLeft ? 1 : 0);
        const boost = k.ShiftLeft || k.ShiftRight ? 2.2 : 1;
        const speed = 9 * boost;

        const dir = new THREE.Vector3();
        const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(ex.pitch, ex.yaw, 0, "YXZ"));
        dir.set(right, up, -fwd).applyQuaternion(q);
        if (dir.lengthSq() > 0) dir.normalize();
        ex.vel.lerp(dir.multiplyScalar(speed), Math.min(1, dt * 6));
        this.camera.position.addScaledVector(ex.vel, dt);
        this.camera.position.x = THREE.MathUtils.clamp(this.camera.position.x, -55, 55);
        this.camera.position.z = THREE.MathUtils.clamp(this.camera.position.z, -360, 20);
        this.camera.position.y = THREE.MathUtils.clamp(this.camera.position.y, 1.0, 48);
        this.camera.quaternion.setFromEuler(new THREE.Euler(ex.pitch, ex.yaw, 0, "YXZ"));
        ex.blend = 1;
    }

    tick() {
        this.raf = requestAnimationFrame(this.tick);
        const rawDt = this.clock.getDelta();
        const dt = Math.min(rawDt, 0.05);
        this.elapsed += dt;
        this.time += dt;
        const t = this.time;

        // wall-clock damping — settles in ~1s even at low frame rates
        const dampK = Math.min(1, rawDt * 4);
        this.progress += (scrollState.progress - this.progress) * dampK;
        const p = THREE.MathUtils.clamp(this.progress, 0, 1);

        const actIdx = SCENES.reduce((acc, s, i) => (p >= s.p - 0.06 ? i : acc), 0);
        if (actIdx !== this.lastAct) {
            this.lastAct = actIdx;
            this.opts.onActChange?.(actIdx);
        }

        const mood = sampleMood(p);
        this.skyMat.uniforms.topColor.value.set(mood.a.top).lerp(new THREE.Color(mood.b.top), mood.k);
        this.skyMat.uniforms.bottomColor.value.set(mood.a.bot).lerp(new THREE.Color(mood.b.bot), mood.k);
        this.fog.color.copy(this.skyMat.uniforms.bottomColor.value);
        this.renderer.setClearColor(this.skyMat.uniforms.bottomColor.value);
        this.sun.color.set(mood.a.sun).lerp(new THREE.Color(mood.b.sun), mood.k);
        this.sun.intensity = mood.a.i + (mood.b.i - mood.a.i) * mood.k;

        const introK = Math.min(1, this.elapsed / 2.8);
        const introE = 1 - Math.pow(1 - introK, 3);
        this.pointerSmooth.x += (this.pointer.x - this.pointerSmooth.x) * Math.min(1, rawDt * 3);
        this.pointerSmooth.y += (this.pointer.y - this.pointerSmooth.y) * Math.min(1, rawDt * 3);

        if (this.explore.active || this.explore.blend < 1) {
            this.updateExplore(dt);
            this.tmpB.copy(this.camera.position);
        } else {
            this.camCurve.getPoint(p, this.tmpA);
            this.lookCurve.getPoint(Math.min(1, p + 0.012), this.tmpB);
            this.tmpA.lerpVectors(INTRO_POS, this.tmpA, introE);
            this.tmpB.lerpVectors(INTRO_LOOK, this.tmpB, introE);
            const swayX = Math.sin(t * 0.32) * 0.35 * introE + this.pointerSmooth.x * 1.2;
            const swayY = Math.cos(t * 0.45) * 0.18 * introE - this.pointerSmooth.y * 0.7;
            this.camera.position.set(this.tmpA.x + swayX, this.tmpA.y + swayY, this.tmpA.z);
            this.camera.lookAt(this.tmpB.x, this.tmpB.y + swayY * 0.4, this.tmpB.z);
            const tangent = this.camCurve.getTangent(Math.min(1, p + 0.001));
            this.camera.rotateZ(THREE.MathUtils.clamp(-tangent.x * 0.8, -0.14, 0.14));
        }
        this.explore.blend = this.explore.active ? 1 : Math.min(1, this.explore.blend + dt * 1.2);

        // shadow light follows the gaze
        this.sun.position.set(this.tmpB.x + 24, 34, this.tmpB.z + 10);
        this.sun.target.position.copy(this.tmpB);
        this.sun.target.updateMatrixWorld();

        // the plane follows the scroll so the flight chapter always frames it
        if (this.plane) {
            const planeP = THREE.MathUtils.clamp((p - 0.26) / 0.26, 0, 1);
            const pz = -96 - planeP * 76;
            const climbK = THREE.MathUtils.clamp((-pz - 126) / 24, 0, 1);
            this.plane.position.set(
                Math.sin(t * 0.7) * 0.4,
                Math.pow(climbK, 0.75) * 13 + 0.15 + Math.sin(t * 1.4) * 0.18,
                pz
            );
            this.plane.rotation.x = -0.3 * climbK;
            this.plane.rotation.z = Math.sin(t * 0.9) * 0.05;
        }

        // the map marker flies UP → Bengaluru on a loop
        if (this.mapMarker && this.mapRoute) {
            const u = (t * 0.22) % 1;
            this.mapRoute.getPoint(u, this.tmpV);
            this.mapMarker.position.copy(this.tmpV).add(V(0, 0, 0.34));
            const tan = this.mapRoute.getTangent(u);
            this.mapMarker.rotation.z = Math.atan2(tan.y, tan.x) - Math.PI / 2;
        }
        if (this.map) this.map.rotation.z = Math.sin(t * 0.4) * 0.03;

        // birds circle and flap over the fields
        this.birds.forEach((bird) => {
            const ud = bird.userData;
            ud.a += dt * ud.speed;
            bird.position.set(ud.cx + Math.cos(ud.a) * ud.r, ud.h + Math.sin(t * 0.7 + ud.r) * 0.5, ud.cz + Math.sin(ud.a) * ud.r);
            bird.rotation.y = -ud.a;
            const flap = Math.sin(t * 10 + ud.r) * 0.5;
            ud.wl.rotation.x = flap;
            ud.wr.rotation.x = -flap;
        });

        // characters come alive
        this.characters.forEach((ch, i) => {
            const kind = ch.group.userData.kind;
            ch.parts.torso.scale.y = 1 + Math.sin(t * 2 + i) * 0.02;
            if (kind === "wave") ch.parts.armR.rotation.z = -2.3 + Math.sin(t * 4) * 0.3;
            if (kind === "write") ch.parts.head.rotation.x = 0.25 + Math.sin(t * 1.2) * 0.06;
            if (kind === "walk") {
                const sw = Math.sin(t * 4.6 + i * 2);
                ch.parts.legL.rotation.x = sw * 0.5;
                ch.parts.legR.rotation.x = -sw * 0.5;
                ch.parts.armL.rotation.x = -sw * 0.4;
                ch.parts.armR.rotation.x = sw * 0.4;
            }
            if (kind === "type") {
                ch.parts.armR.rotation.x = -1.2 + Math.sin(t * 9) * 0.06;
                ch.parts.armL.rotation.x = -1.2 + Math.sin(t * 8 + 1) * 0.06;
            }
        });

        // hover pulse
        this.hotspots.forEach((h) => {
            const target = h === this.hovered ? h.userData.baseScale * 1.05 : h.userData.baseScale;
            h.scale.setScalar(THREE.MathUtils.lerp(h.scale.x, target, Math.min(1, dt * 8)));
        });

        // floating gallery panels bob; the loader ring spins
        this.panels.forEach((panel, i) => {
            panel.position.y = panel.userData.baseY + Math.sin(t * 0.9 + i * 1.4) * 0.16;
        });
        if (this.loaderRing) this.loaderRing.rotation.z += dt * 3.2;

        // rain / dust / stars
        const rainW = zoneW(p, 0.68, 0.74, 0.84, 0.9);
        this.rainMat.opacity = rainW * 0.55;
        if (rainW > 0) {
            const rp = this.rain.geometry.attributes.position;
            for (let i = 0; i < this.rainCount; i++) {
                let y = rp.getY(i) - dt * 24;
                if (y < 0) y += 26;
                rp.setY(i, y);
            }
            rp.needsUpdate = true;
        }
        const dustW = zoneW(p, -1, -0.5, 0.18, 0.28);
        this.dustMat.opacity = dustW * 0.35;
        if (dustW > 0) {
            const dp = this.dust.geometry.attributes.position;
            for (let i = 0; i < this.dustCount; i++) {
                let x = dp.getX(i) + dt * 0.5;
                if (x > 26) x -= 52;
                dp.setX(i, x);
            }
            dp.needsUpdate = true;
        }
        this.starMat.opacity = zoneW(p, 0.93, 0.965, 2, 3) * 0.9;

        // clouds
        const stormW = zoneW(p, 0.56, 0.68, 0.82, 0.92);
        this.cloudMat.color.set("#ffffff").lerp(new THREE.Color("#7d8280"), stormW);
        this.clouds.forEach((cloud, i) => {
            cloud.position.x += dt * (0.5 + (i % 3) * 0.22);
            if (cloud.position.x > 48) cloud.position.x = -48;
        });

        // water shimmers gently
        if (this.pondMat) this.pondMat.opacity = 0.86 + Math.sin(t * 1.3) * 0.07;

        // the dawn sun rises, then dips as the gallery arrives at dusk
        const sunW = zoneW(p, 0.78, 0.88, 0.94, 0.98);
        this.sunSphereMat.opacity = sunW;
        this.haloMat.opacity = sunW * 0.16;
        this.sunSphere.position.y = 7 + sunW * 3;

        this.renderer.render(this.scene, this.camera);
    }

    dispose() {
        cancelAnimationFrame(this.raf);
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("pointermove", this.onPointer);
        window.removeEventListener("mousedown", this.onMouseDown);
        window.removeEventListener("mouseup", this.onMouseUp);
        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("keyup", this.onKeyUp);
        window.removeEventListener("click", this.onClick);
        this.renderer.domElement.removeEventListener("touchstart", this.onTouchStart);
        this.renderer.domElement.removeEventListener("touchmove", this.onTouchMove);
        this.scene.traverse((obj) => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
                const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
                mats.forEach((m) => {
                    if (m.map) m.map.dispose();
                    m.dispose();
                });
            }
        });
        this.renderer.dispose();
        if (this.renderer.domElement.parentElement === this.container) {
            this.container.removeChild(this.renderer.domElement);
        }
    }
}

const INTRO_POS = V(11, 9, 21);
const INTRO_LOOK = V(2, 2.5, -6);

export default JourneyScene;
